import _ from 'lib/underscore';
import * as location from './location';
import {asString} from "./var";

export const BASE_URL = (window.base_url || '/').replace(/(.*)\/\/$|([^/])$/, '$1/');

const isAbsoluteUrlRegex = /^https?:\/\//i;

/**
 * @param {string|object} url Url path
 * @param {string|object} more Additional parts
 * @return {string}
 */
export function siteUrl(url, ...more) {
	if (isAbsoluteUrlRegex.test(url)) {
		if (more.length === 0) {
			return url;
		} else {
			return joinPath.apply(null, [url, ...more]);
		}
	} else {
		return joinPath.apply(null, [BASE_URL, url, ...more]);
	}
}

/**
 * Joins given values with slash.
 * If last value is an object - it will be formatted as query data.
 * @param {string|object} values
 * @return {string}
 */
export function joinPath(...values) {
	if (values.length == 0) return '';
	let query = {};
	let queryString = '';
	let fragment = '';
	let components = [];
	const processValue = (v) => {
		if (_.isObject(v)) {
			query = {...query, ...v};
		} else {
			v = asString(v).trim();
			let i;
			if ((i = v.indexOf('#')) >= 0) {
				fragment = v.substr(i + 1);
				v = v.substr(0, i);
			} else if ((i = v.indexOf('?')) >= 0) {
				queryString = v.substr(i + 1);
				v = v.substr(0, i);
			}
			if (v) {
				if (components.length == 0) {
					v = v.replace(/\/$/g, '');
				} else {
					v = v.replace(/^\/|\/$/g, '');
				}
				components.push(v);
			}
		}
	};
	values.forEach(v => {
		if (_.isArray(v)) {
			v.forEach(processValue);
		} else {
			processValue(v);
		}
	});
	let url = components.join('/');
	if (!_.isEmpty(query)) {
		const q = buildQuery(query);
		if (queryString) {
			queryString += '&';
		}
		queryString += q;
	}
	if (queryString) {
		url += '?' + queryString;
	}
	if (fragment) {
		url += '#' + fragment;
	}
	return url;
}

/**
 *
 * @param {string} url
 * @return {boolean}
 */
export function isAbsolute(url) {
	return isAbsoluteUrlRegex.test(url);
}

/**
 * Builds query string from given object
 * @param {object} queryData
 * @return {string}
 */
export function buildQuery(queryData) {
	return location.buildQuery(queryData);
}

export function highlightUrls(text, maxlength = 30) {
	return text.replace(/(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\?+=&%#\/\wА-Яа-яёЁ\.;-]*)*\/?/g, function (s) {
        var st = s;

        if (s.length > maxlength) {
            st = s.slice(0, maxlength) + "...";
        }

        if (!/^(https?:\/\/)/.test(s)) {
            s = "http://" + s;
        }

        return "<a href='" + s + "' target='_blank' rel='nofollow noopener'>" + st + "</a>";
    })
}

export class UrlParser {
	/**
	 * @param {string} url
	 */
	constructor(url) {
		this._a = document.createElement('a');
		this._a.href = url;
	}

	/** @return {string} */
	get origin() {
		return this._a.origin;
	}

	/** @return {string} */
	get protocol() {
		return this._a.protocol;
	}

	/** @return {string} */
	get host() {
		return this._a.host;
	}

	/** @return {string} */
	get hostname() {
		return this._a.hostname;
	}

	/** @return {string} */
	get port() {
		return this._a.port;
	}

	/** @return {string} */
	get pathname() {
		return this._a.pathname;
	}

	/** @return {string} */
	get search() {
		return this._a.search;
	}

	/** @return {string} */
	get hash() {
		return this._a.hash;
	}
}

export class UrlBuilder {
	constructor(protocol = null, host = null, port = null, path = null, query = null, hash = null) {
		this._protocol = protocol;
		this._host = host;
		this._port = port;
		this._path = path;
		this._query = query;
		this._hash = hash;
	}

	/**
	 * @param {string|null} value
	 */
	set protocol(value) {
		this._protocol = postfix(value, ':');
	}

	/** @return {string|null} */
	get protocol() {
		return this._protocol;
	}

	/**
	 * @param {string|null} value
	 */
	set host(value) {
		this._host = value;
	}

	/** @return {string|null} */
	get host() {
		return this._host;
	}

	/**
	 * @param {string|number|null} value
	 */
	set port(value) {
		this._port = value;
	}

	/** @return {string|number|null} */
	get port() {
		return this._port;
	}

	/**
	 * @param {string|null} value
	 */
	set path(value) {
		this._path = value;
	}

	/** @return {string|null} */
	get path() {
		return this._path;
	}

	/**
	 * @param {string|object|null} value
	 */
	set query(value) {
		this._query = prefix(value, '?');
	}

	/** @return {string|object|null} */
	get query() {
		return this._query;
	}

	/**
	 * @param {string|null} value
	 */
	set hash(value) {
		this._hash = prefix(value, '#');
	}

	/** @return {string|null} */
	get hash() {
		return this._hash;
	}

	/**
	 * @return {string}
	 */
	build() {
		const origin = (this._protocol ? this._protocol + '//' : '') +
			(this._host ? this._host + (this._port ? ':' + this._port : '') : '');
		const query = typeof(this._query) === 'object' ? prefix(buildQuery(this._query), '?') : this._query;
		return joinPath(origin, this._path, query, this._hash);
	}
}

function prefix(val, prefix) {
	if (val && typeof(val) === 'string' && !val.startsWith(prefix)) {
		return prefix + val;
	}
	return val;
}

function postfix(val, postfix) {
	if (val && typeof(val) === 'string' && !val.endsWith(postfix)) {
		return val + postfix;
	}
	return val;
}



// WEBPACK FOOTER //
// ./core/utils/url.js